import fs from 'fs-extra';
import path from 'path';
import { WORKFLOW_DIR } from '../../constants.js';

export interface Tag {
  id: string;
  name: string;
  category: 'project' | 'module' | 'team' | 'custom';
  color: string;
  description?: string;
  count?: number;
  created: string;
  updated: string;
}

export class TagService {
  private tagsPath: string;

  constructor() {
    this.tagsPath = path.join(process.cwd(), WORKFLOW_DIR, 'tags.json');
    this.ensureTagsFile();
  }

  private async ensureTagsFile() {
    await fs.ensureFile(this.tagsPath);
    const exists = await fs.pathExists(this.tagsPath);
    if (!exists || (await fs.readFile(this.tagsPath, 'utf-8')).trim() === '') {
      // 初始化一些默认标签
      const defaultTags: Tag[] = [
        {
          id: 'tag-1',
          name: 'Gongfu',
          category: 'project',
          color: 'bg-blue-500',
          description: 'Gongfu 核心项目',
          count: 0,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'tag-2',
          name: 'UI',
          category: 'module',
          color: 'bg-purple-500',
          description: '前端界面相关',
          count: 0,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'tag-3',
          name: 'Backend',
          category: 'module',
          color: 'bg-green-500',
          description: '后端服务相关',
          count: 0,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'tag-4',
          name: 'CLI',
          category: 'module',
          color: 'bg-orange-500',
          description: '命令行工具相关',
          count: 0,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'tag-5',
          name: '前端组',
          category: 'team',
          color: 'bg-cyan-500',
          description: '前端开发团队',
          count: 0,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'tag-6',
          name: '后端组',
          category: 'team',
          color: 'bg-pink-500',
          description: '后端开发团队',
          count: 0,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'tag-7',
          name: '紧急',
          category: 'custom',
          color: 'bg-red-500',
          description: '需要立即处理的任务',
          count: 0,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'tag-8',
          name: '优化',
          category: 'custom',
          color: 'bg-yellow-500',
          description: '性能优化相关',
          count: 0,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        }
      ];
      await fs.writeJson(this.tagsPath, defaultTags, { spaces: 2 });
    }
  }

  async list(): Promise<Tag[]> {
    try {
      const tags = await fs.readJson(this.tagsPath);
      return tags;
    } catch (error) {
      console.error('Error reading tags:', error);
      return [];
    }
  }

  async get(id: string): Promise<Tag | null> {
    const tags = await this.list();
    return tags.find(tag => tag.id === id) || null;
  }

  async create(tagData: Omit<Tag, 'id' | 'created' | 'updated' | 'count'>): Promise<Tag> {
    const tags = await this.list();
    
    // 检查名称是否重复
    if (tags.some(tag => tag.name === tagData.name)) {
      throw new Error('标签名称已存在');
    }

    const newTag: Tag = {
      ...tagData,
      id: `tag-${Date.now()}`,
      count: 0,
      created: new Date().toISOString(),
      updated: new Date().toISOString()
    };

    tags.push(newTag);
    await fs.writeJson(this.tagsPath, tags, { spaces: 2 });
    
    return newTag;
  }

  async update(id: string, updates: Partial<Tag>): Promise<Tag> {
    const tags = await this.list();
    const index = tags.findIndex(tag => tag.id === id);
    
    if (index === -1) {
      throw new Error('标签不存在');
    }

    // 如果更新名称，检查是否重复
    if (updates.name && updates.name !== tags[index].name) {
      if (tags.some(tag => tag.name === updates.name && tag.id !== id)) {
        throw new Error('标签名称已存在');
      }
    }

    const updatedTag = {
      ...tags[index],
      ...updates,
      id,
      created: tags[index].created,
      updated: new Date().toISOString()
    };

    tags[index] = updatedTag;
    await fs.writeJson(this.tagsPath, tags, { spaces: 2 });
    
    return updatedTag;
  }

  async delete(id: string): Promise<void> {
    const tags = await this.list();
    const filteredTags = tags.filter(tag => tag.id !== id);
    
    if (filteredTags.length === tags.length) {
      throw new Error('标签不存在');
    }

    await fs.writeJson(this.tagsPath, filteredTags, { spaces: 2 });
  }

  // 更新标签使用计数
  async updateCount(tagId: string, count: number): Promise<void> {
    const tags = await this.list();
    const tag = tags.find(t => t.id === tagId);
    
    if (tag) {
      tag.count = count;
      tag.updated = new Date().toISOString();
      await fs.writeJson(this.tagsPath, tags, { spaces: 2 });
    }
  }
}